import { useEffect } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCamera, faPenNib, faGalleryThumbnails, faGrid2,
} from '@fortawesome/pro-solid-svg-icons'

import { useSetState } from '@domain-group/react-hooks'

import {
  Box, DashboardModule, Link, Text,
} from '@domain-group/suit-theme'

import useBrand from '@hooks/useBrand'
import useCarousel from '@hooks/useCarousel'

import type { UseCampaignPayload } from '@hooks/useCampaign'

import type { ImageModel } from '@models/types'

import BrandedModuleHeader from '@sections/Public/packs/Campaign/components/BrandedModuleHeader'

import useAdCopyReviewer from './AdCopy/hooks/useAdCopyReviewer'

import AdCopy from './AdCopy'
import Images from '../Images'
import MediaGrid from './MediaGrid'
import MediaReviewerActions from './MediaReviewerActions'
import TabButton from './TabButton'

interface MediaAndAdCopyProps {
  brandId: number,
  callbacks: {
    downloadMedia: UseCampaignPayload['callbacks']['downloadMedia'],
  },
  campaignId: number,
  filteredImages: ImageModel[],
  hasFilteredImages: boolean,
  queryImageIds?: number[],
  sharedAdCopyTypes: string[],
}

const defaultState = {
  gridView: false,
  showMedia: true,
}

const MediaAndAdCopy = (props: MediaAndAdCopyProps) => {
  const {
    brandId,
    callbacks: {
      downloadMedia,
    },
    campaignId,
    filteredImages,
    hasFilteredImages,
    queryImageIds,
    sharedAdCopyTypes,
  } = props

  const [state, setState] = useSetState(defaultState)
  const { gridView, showMedia } = state

  const brandPayload = useBrand({ id: brandId })
  const { brand, publicLinkTextAccentColour } = brandPayload

  const carouselPayload = useCarousel({ initialIndex: 0, items: filteredImages })
  const { callbacks: { setSelectedIndex } } = carouselPayload

  const imageIds = filteredImages.map(image => image.id)

  const generalDisclaimer = brand?.options?.branded_public_links?.general_disclaimer || null

  const { hasSharedAdCopyTypes } = useAdCopyReviewer({ campaignId, sharedAdCopyTypes })
  // display 'ad copy' tab on page load if no images provided
  useEffect(() => {
    if (!queryImageIds?.length && hasSharedAdCopyTypes){
      setState({ ...state, showMedia: false })
    }
  }, [])

  return (
    <>
      <DashboardModule
        boxProps={{
          border: 'none', marginTop: 'large', marginX: 'large', backgroundColor: 'backgroundColor',
        }}
        contentBoxProps={{ flexDirection: 'column' }}
        customHeader={(
          <BrandedModuleHeader
            accentColor={publicLinkTextAccentColour}
            backgroundColour="backgroundColor"
            title="Media & Ad Copy Proofing"
          />
        )}
      />

      <Box flexDirection="column" justifyContent="center">
        <Box paddingX="large" width="300px" alignSelf="start">
          {hasFilteredImages && (
            <TabButton
              accentColor={publicLinkTextAccentColour}
              icon={faCamera}
              onClick={() => setState({ ...state, showMedia: true })}
              selected={showMedia}
              text="Photos & Plans"
            />
          )}

          {hasSharedAdCopyTypes && (
            <TabButton
              accentColor={publicLinkTextAccentColour}
              icon={faPenNib}
              onClick={() => setState({ ...state, showMedia: false })}
              selected={!showMedia}
              text="Ad Copy"
            />
          )}
        </Box>

        <Box alignSelf="start" marginTop="medium" paddingX="large">
          <Text fontSize="xsmall" lineHeight="1.25" color="bodyFontLightColor">
            {generalDisclaimer}
          </Text>
        </Box>

        {showMedia && (
          <Box
            alignSelf="end"
            justifyContent="end"
            marginBottom="medium"
            marginTop="large"
            paddingTop="medium"
            paddingX="large"
          >
            <Link
              onClick={() => setState({ ...state, gridView: !gridView })}
              style={{
                alignItems: 'center',
                color: publicLinkTextAccentColour,
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                gap: '6px',
                textDecoration: 'none',
              }}
            >
              <Text fontSize="xsmall" fontWeight={500} color={publicLinkTextAccentColour}>
                {gridView ? 'Carousel View' : 'Grid View'}
              </Text>

              <Text
                color={publicLinkTextAccentColour}
                fontSize="xsmall"
                fontWeight={500}
              >
                <FontAwesomeIcon
                  icon={gridView ? faGalleryThumbnails : faGrid2}
                />
              </Text>
            </Link>
          </Box>
        )}
      </Box>

      {hasFilteredImages && !gridView && showMedia && (
        <Images
          brandId={brandId}
          callbacks={{ downloadMedia }}
          carouselPayload={carouselPayload}
          filteredImages={filteredImages}
        />
      )}

      {hasFilteredImages && gridView && showMedia && (
        <MediaGrid callbacks={{ downloadMedia, setSelectedIndex, setState }} filteredImages={filteredImages} />
      )}

      {showMedia && hasFilteredImages && (
        <MediaReviewerActions
          brandId={brandId}
          callbacks={{ downloadMedia }}
          campaignId={campaignId}
          imageIds={imageIds}
        />
      )}

      {!showMedia && (
        <AdCopy sharedAdCopyTypes={sharedAdCopyTypes} campaignId={campaignId} />
      )}
    </>
  )
}

export default MediaAndAdCopy
